import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { hasMinRole, useAuth } from "@/contexts/AuthContext";
import {
  Flag, Users, Star, Plus, CheckCircle, XCircle, Trash,
  FileText,
} from "@phosphor-icons/react";

const FORM_TITLES = {
  "F-PM-11": "Training / Knowledge Transfer",
  "F-PM-14": "Client Sign-Off / Handover",
  "F-PM-16": "Equipment Return",
  "F-PM-18": "Archive Manifest",
  "F-PM-20": "Project Close-out Memo",
  "F-PM-21": "Commercial Variance Analysis",
  "F-PM-22": "Case Study",
  "F-PM-23": "Client Reference / Testimonial",
  "F-PM-24": "Post-Project Review Memo",
};

const FORM_FIELDS = {
  "F-PM-11": [
    ["session_date", "Session date", "date"],
    ["topic", "Topic", "input"],
    ["attendees", "Attendees (comma-separated)", "textarea"],
    ["trainer", "Trainer", "input"],
    ["materials_url", "Materials link", "input"],
  ],
  "F-PM-14": [
    ["handover_date", "Handover date *", "date"],
    ["client_representative", "Client representative *", "input"],
    ["deliverables_handed_over", "Deliverables handed over *", "textarea"],
    ["outstanding_items", "Outstanding items", "textarea"],
    ["client_signoff", "Client sign-off (Y/N) *", "input"],
  ],
  "F-PM-16": [
    ["return_date", "Return date", "date"],
    ["items_csv", "Items returned (one per line)", "textarea"],
    ["receiver", "Received by", "input"],
    ["condition_notes", "Condition notes", "textarea"],
  ],
  "F-PM-18": [
    ["onedrive_path", "Archive folder path *", "input"],
    ["items_indexed", "Number of items indexed", "number"],
    ["retention_years", "Retention period (years)", "number"],
    ["custodian", "Custodian", "input"],
  ],
  "F-PM-20": [
    ["final_outcome", "Final outcome *", "textarea"],
    ["key_achievements", "Key achievements", "textarea"],
    ["financial_summary", "Financial summary", "textarea"],
    ["lessons_summary", "Lessons summary", "textarea"],
    ["pd_signature", "PD signature line", "input"],
  ],
  "F-PM-21": [
    ["budgeted_value", "Budgeted value (USD)", "number"],
    ["actual_value", "Actual value (USD)", "number"],
    ["variance_pct", "Variance %", "number"],
    ["root_cause", "Root cause *", "textarea"],
    ["mitigation_for_next", "Mitigation for next project", "textarea"],
  ],
  "F-PM-22": [
    ["headline", "Headline", "input"],
    ["challenge", "Challenge", "textarea"],
    ["approach", "Approach", "textarea"],
    ["outcome", "Outcome", "textarea"],
    ["client_quote", "Client quote", "textarea"],
  ],
  "F-PM-23": [
    ["client_name", "Client name", "input"],
    ["title", "Title", "input"],
    ["testimonial", "Testimonial *", "textarea"],
    ["permission_to_publish", "Permission to publish (Y/N)", "input"],
  ],
  "F-PM-24": [
    ["spi_at_close", "SPI at close", "number"],
    ["cpi_at_close", "CPI at close", "number"],
    ["csat_at_close", "CSAT score at close", "number"],
    ["nps_at_close", "NPS at close", "number"],
    ["coo_remarks", "COO remarks *", "textarea"],
    ["repeat_business_potential", "Repeat business potential (H/M/L)", "input"],
  ],
};

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  archived: "bg-slate-200 text-slate-500",
};

const QUAD_TONE = {
  manage_closely: "bg-red-100 text-red-800 border-red-200",
  keep_satisfied: "bg-amber-100 text-amber-800 border-amber-200",
  keep_informed: "bg-blue-100 text-blue-800 border-blue-200",
  monitor: "bg-slate-100 text-slate-600 border-slate-200",
};

const QUAD_LABEL = {
  manage_closely: "Manage closely",
  keep_satisfied: "Keep satisfied",
  keep_informed: "Keep informed",
  monitor: "Monitor",
};

export default function ProjectCloseoutCenter({ project, trigger }) {
  const { user } = useAuth();
  const canEdit = hasMinRole(user, "manager");
  const canApprove = hasMinRole(user, "admin");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("forms");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid={`closeout-${project.id}`}>
            <Flag size={12} className="mr-1" /> Close-out
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="closeout-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Flag size={18} className="text-purple-600" />
            Close-out · Stakeholders · Post-Project · {project.name}
          </DialogTitle>
          <DialogDescription>
            F-PM-11/14/16/18/20/21/22/23/24 forms · F-PM-19 CSAT · Stakeholder register
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {[
            ["forms", "Close-out Forms", FileText],
            ["csat", "Client Satisfaction (CSAT)", Star],
            ["stakeholders", "Stakeholders", Users],
          ].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition ${tab === id ? "border-[#1E5CBA] text-[#1E5CBA]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`closeout-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "forms" && <FormsTab project={project} canEdit={canEdit} canApprove={canApprove} />}
        {tab === "csat" && <CsatTab project={project} canEdit={canEdit} />}
        {tab === "stakeholders" && <StakeholdersTab project={project} canEdit={canEdit} canApprove={canApprove} />}
      </DialogContent>
    </Dialog>
  );
}

const Loading = () => <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
const Empty = ({ text }) => <div className="dc-card p-10 text-center text-sm text-slate-500">{text}</div>;

/* ─── FORMS TAB ──────────────────────────────────────────────────────── */
function FormsTab({ project, canEdit, canApprove }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/closeout-forms?project_id=${project.id}`);
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{rows.length} close-out forms</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="closeout-form-create">
            <Plus size={11} className="mr-1" /> New form
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No close-out forms yet. Add F-PM-14 first when client hands over." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Form</th><th className="px-3 py-2">Approver</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Updated</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((f) => (
                <tr key={f.id} onClick={() => { setEdit(f); setShowForm(true); }} className="cursor-pointer hover:bg-slate-50" data-testid={`closeout-row-${f.id}`}>
                  <td className="px-3 py-2 font-mono-alt text-xs font-semibold">{f.form_code}</td>
                  <td className="px-3 py-2">{f.title}</td>
                  <td className="px-3 py-2 text-xs">{f.approver_role || "PD"}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${STATUS_TONE[f.status]}`}>{f.status}</span></td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">{new Date(f.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <CloseoutFormDialog projectId={project.id} form={edit} canApprove={canApprove} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function CloseoutFormDialog({ projectId, form, canApprove, onClose, onSaved }) {
  const isEdit = !!form?.id;
  const [code, setCode] = useState(form?.form_code || "F-PM-14");
  const [fields, setFields] = useState(form?.fields || {});
  const [approverRole, setApproverRole] = useState(form?.approver_role || "PD");
  const [notes, setNotes] = useState(form?.notes || "");
  const [busy, setBusy] = useState(false);

  const template = FORM_FIELDS[code] || [];
  const status = form?.status || "draft";

  const submit = async (action) => {
    setBusy(true);
    try {
      let id = form?.id;
      if (isEdit) {
        await api.patch(`/closeout-forms/${id}`, { fields, notes, approver_role: approverRole });
      } else {
        const { data } = await api.post("/closeout-forms", {
          project_id: projectId, form_code: code, fields, notes,
          approver_role: approverRole,
        });
        id = data.id;
      }
      if (action === "submit") {
        await api.post(`/closeout-forms/${id}/submit`);
        toast.success("Form submitted for approval");
      } else if (action === "approve") {
        await api.post(`/closeout-forms/${id}/transition`, { decision: "approve", comment: notes });
        toast.success("Form approved");
      } else if (action === "reject") {
        await api.post(`/closeout-forms/${id}/transition`, { decision: "reject", comment: notes });
        toast.success("Form rejected");
      } else {
        toast.success("Form saved");
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText size={16} /> {isEdit ? `${code} — ${FORM_TITLES[code]}` : "New close-out form"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${STATUS_TONE[status]}`}>{status}</span> : "Select form code → fill fields → submit for approval"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {!isEdit && (
            <div className="col-span-2">
              <Label>Form code *</Label>
              <Select value={code} onValueChange={(v) => { setCode(v); setFields({}); }}>
                <SelectTrigger data-testid="closeout-code-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FORM_TITLES).map(([c, t]) => (
                    <SelectItem key={c} value={c}>{c} — {t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Approver role</Label>
            <Select value={approverRole} onValueChange={setApproverRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PD">PD</SelectItem>
                <SelectItem value="COO">COO</SelectItem>
                <SelectItem value="CEO">CEO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div></div>
          {template.map(([k, label, type]) => (
            <div key={k} className={type === "textarea" ? "col-span-2" : ""}>
              <Label className="text-[11px]">{label}</Label>
              {type === "textarea"
                ? <Textarea rows={2} value={fields[k] || ""} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} data-testid={`closeout-field-${k}`} />
                : <Input type={type} value={fields[k] || ""} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} data-testid={`closeout-field-${k}`} />
              }
            </div>
          ))}
          <div className="col-span-2">
            <Label>Notes / comment</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {(status === "draft" || !isEdit) && (
            <>
              <Button variant="outline" disabled={busy} onClick={() => submit("save")} data-testid="closeout-save-draft">Save draft</Button>
              {isEdit && <Button disabled={busy} className="btn-primary-blue" onClick={() => submit("submit")} data-testid="closeout-submit">Submit for approval</Button>}
            </>
          )}
          {status === "submitted" && canApprove && (
            <>
              <Button variant="destructive" disabled={busy} onClick={() => submit("reject")} data-testid="closeout-reject"><XCircle size={11} className="mr-1" /> Reject</Button>
              <Button className="btn-primary-blue" disabled={busy} onClick={() => submit("approve")} data-testid="closeout-approve"><CheckCircle size={11} className="mr-1" /> Approve</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── CSAT TAB ───────────────────────────────────────────────────────── */
function CsatTab({ project, canEdit }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ avg_score: null, nps_score: null });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/csat-surveys?project_id=${project.id}`);
      setRows(data.items || []);
      setStats({ avg_score: data.avg_score, nps_score: data.nps_score });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 text-center">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-emerald-700">Avg CSAT</div>
          <div className="mt-1 font-display text-4xl font-bold tabular-nums text-emerald-900">{stats.avg_score != null ? stats.avg_score : "—"}</div>
          <div className="text-[10px] text-emerald-600 mt-0.5">out of 5.0</div>
        </div>
        <div className={`rounded-xl border-2 p-4 text-center ${stats.nps_score >= 50 ? "border-emerald-200 bg-emerald-50" : stats.nps_score >= 0 ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"}`}>
          <div className="text-[10px] font-mono-alt uppercase tracking-widest opacity-70">NPS Score</div>
          <div className="mt-1 font-display text-4xl font-bold tabular-nums">{stats.nps_score != null ? (stats.nps_score > 0 ? `+${stats.nps_score}` : stats.nps_score) : "—"}</div>
          <div className="text-[10px] opacity-60 mt-0.5">Promoters − Detractors</div>
        </div>
        <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-4 text-center">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-600">Responses</div>
          <div className="mt-1 font-display text-4xl font-bold tabular-nums text-slate-900">{rows.length}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">surveys captured</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">Survey history</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => setShowForm(true)} data-testid="csat-create-btn">
            <Plus size={11} className="mr-1" /> Capture CSAT
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No CSAT surveys captured. F-PM-19 should be sent at P7." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Respondent</th><th className="px-3 py-2">Org</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Avg score</th><th className="px-3 py-2">NPS</th><th className="px-3 py-2">Comment</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`csat-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium">{c.respondent_name || "Anonymous"}</td>
                  <td className="px-3 py-2 text-xs">{c.respondent_org || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono-alt">{c.survey_date}</td>
                  <td className="px-3 py-2 font-display font-bold text-emerald-700 tabular-nums">{c.avg_score?.toFixed(2)}</td>
                  <td className="px-3 py-2">
                    {c.nps != null && (
                      <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase ${c.nps_bucket === "promoter" ? "bg-emerald-100 text-emerald-800" : c.nps_bucket === "passive" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                        {c.nps} · {c.nps_bucket}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 line-clamp-2">{c.open_comments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <CsatForm projectId={project.id} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

const CSAT_DIMS = [
  ["delivery_quality", "Delivery quality"],
  ["schedule_adherence", "Schedule adherence"],
  ["communication", "Communication"],
  ["professionalism", "Professionalism"],
  ["value_for_money", "Value for money"],
  ["would_recommend", "Would recommend"],
];

function CsatForm({ projectId, onClose, onSaved }) {
  const [f, setF] = useState({
    respondent_name: "", respondent_email: "", respondent_org: "",
    survey_date: new Date().toISOString().slice(0, 10),
    ratings: CSAT_DIMS.reduce((a, [k]) => ({ ...a, [k]: 5 }), {}),
    nps: 9, open_comments: "", follow_up_needed: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/csat-surveys", { ...f, project_id: projectId, nps: Number(f.nps) });
      toast.success("CSAT captured");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Star size={16} /> Capture CSAT (F-PM-19)</DialogTitle>
          <DialogDescription>1-5 rating per dimension · NPS 0-10</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><Label>Respondent name</Label><Input value={f.respondent_name} onChange={(e) => setF({ ...f, respondent_name: e.target.value })} /></div>
          <div><Label>Organisation</Label><Input value={f.respondent_org} onChange={(e) => setF({ ...f, respondent_org: e.target.value })} /></div>
          <div><Label>Survey date</Label><Input type="date" value={f.survey_date} onChange={(e) => setF({ ...f, survey_date: e.target.value })} /></div>
          <div><Label>NPS (0-10)</Label><Input type="number" min={0} max={10} value={f.nps} onChange={(e) => setF({ ...f, nps: e.target.value })} data-testid="csat-nps" /></div>
          <div className="col-span-2">
            <div className="overline mb-2">Ratings (1-5)</div>
            {CSAT_DIMS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
                <Label className="flex-1 text-xs">{label}</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setF({ ...f, ratings: { ...f.ratings, [k]: n } })}
                      className={`h-7 w-7 rounded text-xs font-semibold transition ${f.ratings[k] >= n ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                      data-testid={`csat-rate-${k}-${n}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="col-span-2"><Label>Open comments</Label><Textarea rows={3} value={f.open_comments} onChange={(e) => setF({ ...f, open_comments: e.target.value })} /></div>
          <div className="col-span-2 flex items-center justify-between rounded border p-2">
            <Label>Follow-up needed</Label>
            <Switch checked={f.follow_up_needed} onCheckedChange={(v) => setF({ ...f, follow_up_needed: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} className="btn-primary-blue" onClick={submit} data-testid="csat-save">{busy ? "Saving…" : "Save survey"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── STAKEHOLDERS TAB ───────────────────────────────────────────────── */
function StakeholdersTab({ project, canEdit, canApprove }) {
  const [rows, setRows] = useState([]);
  const [grid, setGrid] = useState(null);
  const [quadrants, setQuadrants] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/stakeholders?project_id=${project.id}`),
        api.get(`/projects/${project.id}/power-interest-grid`),
      ]);
      setRows(a.data.items || []);
      setQuadrants(a.data.quadrants || {});
      setGrid(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    if (!confirm("Delete this stakeholder?")) return;
    try {
      await api.delete(`/stakeholders/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Power-Interest grid */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 lg:col-span-2">
          <div className="overline mb-2">Power × Interest matrix (5×5)</div>
          {grid && (
            <div className="flex items-end gap-1">
              <div className="flex flex-col-reverse items-center gap-1 text-[9px] font-mono-alt text-slate-500 mr-1">
                <div className="h-8" />
                {[1, 2, 3, 4, 5].map((l) => <div key={l} className="h-8 flex items-center">{l}</div>)}
                <div className="font-semibold mt-1">P</div>
              </div>
              <div className="flex-1">
                <div className="grid grid-cols-5 gap-1">
                  {[5, 4, 3, 2, 1].flatMap((p) =>
                    [1, 2, 3, 4, 5].map((i) => {
                      const cell = grid.grid?.[p - 1]?.[i - 1] || { count: 0 };
                      const isCloseManaged = p >= 4 && i >= 4;
                      const isMonitor = p < 4 && i < 4;
                      return (
                        <div key={`${p}-${i}`}
                          className={`h-8 rounded text-xs font-bold flex items-center justify-center ${cell.count ? (isCloseManaged ? "bg-red-500 text-white" : isMonitor ? "bg-slate-300 text-slate-700" : "bg-amber-300 text-amber-900") : "bg-slate-100 text-slate-300"}`}
                          title={`Power ${p} × Interest ${i} · ${cell.count}`}>
                          {cell.count || ""}
                        </div>
                      );
                    }))}
                </div>
                <div className="mt-1 grid grid-cols-5 gap-1 text-[9px] font-mono-alt text-slate-500 text-center">
                  {[1, 2, 3, 4, 5].map((i) => <div key={i}>{i}</div>)}
                </div>
                <div className="text-center text-[9px] font-mono-alt text-slate-500 font-semibold mt-0.5">INTEREST →</div>
              </div>
            </div>
          )}
        </div>
        {/* Quadrant counts */}
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="overline mb-2">Quadrants</div>
          {Object.entries(QUAD_LABEL).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
              <span className={`inline-block rounded border px-2 py-0.5 text-[10px] font-mono-alt uppercase ${QUAD_TONE[k]}`}>{label}</span>
              <span className="font-display text-lg font-semibold tabular-nums">{quadrants[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">{rows.length} stakeholders</h4>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue" onClick={() => { setEdit(null); setShowForm(true); }} data-testid="stakeholder-create">
            <Plus size={11} className="mr-1" /> Add stakeholder
          </Button>
        )}
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="No stakeholders registered for this project." /> : (
        <div className="dc-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Name / Org</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">P×I</th><th className="px-3 py-2">Quadrant</th><th className="px-3 py-2">Cadence</th><th className="px-3 py-2">Channel</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`stakeholder-row-${s.id}`}>
                  <td className="px-3 py-2 cursor-pointer" onClick={() => { setEdit(s); setShowForm(true); }}>
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-[10px] text-slate-500">{s.organisation || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{s.role || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono-alt">{s.stakeholder_type}</td>
                  <td className="px-3 py-2 font-mono-alt text-xs">{s.influence}×{s.interest}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded border px-2 py-0.5 text-[10px] font-mono-alt uppercase ${QUAD_TONE[s.quadrant]}`}>{QUAD_LABEL[s.quadrant]}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{s.communication_cadence}</td>
                  <td className="px-3 py-2 text-xs">{s.preferred_channel}</td>
                  <td className="px-3 py-2 text-right">
                    {canApprove && (
                      <Button size="sm" variant="ghost" className="h-6" onClick={() => del(s.id)} data-testid={`stakeholder-del-${s.id}`}>
                        <Trash size={11} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <StakeholderForm projectId={project.id} stake={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function StakeholderForm({ projectId, stake, onClose, onSaved }) {
  const isEdit = !!stake?.id;
  const [f, setF] = useState(stake || {
    name: "", organisation: "", role: "",
    stakeholder_type: "external", influence: 3, interest: 3,
    contact_email: "", contact_phone: "",
    communication_cadence: "monthly", preferred_channel: "email",
    engagement_strategy: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, influence: Number(f.influence), interest: Number(f.interest) };
      if (isEdit) await api.patch(`/stakeholders/${stake.id}`, payload);
      else await api.post("/stakeholders", { ...payload, project_id: projectId });
      toast.success(isEdit ? "Stakeholder updated" : "Stakeholder added");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit stakeholder" : "Add stakeholder"}</DialogTitle>
          <DialogDescription>Power-Interest grid auto-assigns quadrant</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="stake-name" /></div>
          <div><Label>Organisation</Label><Input value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} /></div>
          <div><Label>Role</Label><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></div>
          <div>
            <Label>Type</Label>
            <Select value={f.stakeholder_type} onValueChange={(v) => setF({ ...f, stakeholder_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["internal", "client", "partner", "regulator", "community", "funder", "supplier", "external"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Influence/Power (1-5) *</Label><Input type="number" min={1} max={5} value={f.influence} onChange={(e) => setF({ ...f, influence: e.target.value })} data-testid="stake-influence" /></div>
          <div><Label>Interest (1-5) *</Label><Input type="number" min={1} max={5} value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} data-testid="stake-interest" /></div>
          <div><Label>Contact email</Label><Input value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
          <div><Label>Contact phone</Label><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
          <div>
            <Label>Cadence</Label>
            <Select value={f.communication_cadence} onValueChange={(v) => setF({ ...f, communication_cadence: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["daily", "weekly", "biweekly", "monthly", "quarterly", "adhoc"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Preferred channel</Label>
            <Select value={f.preferred_channel} onValueChange={(v) => setF({ ...f, preferred_channel: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["email", "phone", "meeting", "portal", "sms", "whatsapp"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Engagement strategy</Label><Textarea rows={2} value={f.engagement_strategy} onChange={(e) => setF({ ...f, engagement_strategy: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !f.name} className="btn-primary-blue" onClick={submit} data-testid="stake-save">{busy ? "Saving…" : isEdit ? "Update" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
